import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@oneflowab/pomes';
import type { MessageTranslator } from '@oneflowab/pomes';

import {
  isUserOnAdminPage,
  isUserOnGlobalSearchPage,
  isUserOnGlobalTrashPage,
  isUserOnContractPage,
  isUserOnTemplatePage,
} from 'utils/isOnPage';
import { selectWorkspace, getCurrentWorkspaceSelector } from 'reducers/app';
import { getAllWorkspacesFromSessionSelector } from 'reducers/session';
import { withWorkspaceNamespace } from 'hocs/with-current-workspace/with-workspace-namespace';
import { amplitudeLogEvent } from 'client-analytics/amplitude';

import EmptyState from 'components/empty-state';
import NoWorkspacesIcon from 'components/icons/no-workspaces';
import SelectDropdown from 'components/select-dropdown';
import SidebarTooltip from 'components/sidebar/sidebar-tooltip';
import WorkspaceIcon from 'components/icons/workspace';
import CheckIcon from 'components/icons/check';

import style from './workspace-dropdown.module.scss';

type Props = {
  expanded: boolean,
  message: MessageTranslator,
  tooltipText?: string,
  isMobile?: boolean,
  toggleMobileMenu: () => void,
};

const WorkspaceDropdownComponent = ({
  expanded,
  message,
  tooltipText,
  isMobile,
  toggleMobileMenu,
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const currentWorkspace = useSelector(getCurrentWorkspaceSelector);
  const workspaces = useSelector(getAllWorkspacesFromSessionSelector);
  const workspaceLocation = withWorkspaceNamespace({ id: currentWorkspace.id });

  const isOnContractsPage = window.location.pathname.includes('/contracts/all');
  const searchParams = new URLSearchParams(location.search);
  const hasSearchQuery = searchParams.size > 0;
  const isOnTemplatePage = isUserOnTemplatePage(pathname);
  const isOnAdminPage = isUserOnAdminPage(pathname);
  const isOnGlobalSearchPage = isUserOnGlobalSearchPage(pathname);

  const getWorkspaceName = useCallback(
    (workspace: Workspace) => {
      if (workspace.virtual) {
        return message({
          id: 'Shared with me',
          comment: 'The virtual workspace name',
        });
      }
      return workspace.name;
    },
    [message],
  );

  const toDropdownOption = useCallback(
    (workspace: Workspace) => {
      const icon =
        currentWorkspace?.id === workspace.id ? (
          <CheckIcon height="13px" />
        ) : (
          <WorkspaceIcon height="13px" />
        );

      return {
        label: getWorkspaceName(workspace),
        IconComponent: () => icon,
        id: workspace.id,
      };
    },
    [currentWorkspace, getWorkspaceName],
  );

  const renderEmptyState = useCallback(() => (
    <EmptyState
      icon={<NoWorkspacesIcon height="33px" />}
      header={message({
        id: 'No workspaces available',
        comment:
          'Empty state header in the workspace dropdown when not having access to any workspaces.',
      })}
      content={message({
        id: 'You need to be granted access to a workspace to see it in this dropdown.',
        comment:
          'Empty state text in the workspace dropdown when not having access to any workspaces.',
      })}
      defaultStyle
      className={style.EmptyState}
    />
  ), [message]);

  const renderSelectedItem = useCallback(() => {
    const isOnGlobalTrashSearchPage = isUserOnGlobalTrashPage(pathname);
    const isOnAdminOrGlobalSearchPage = isOnAdminPage
    || isOnGlobalSearchPage || isOnGlobalTrashSearchPage;

    const workspaceName = isOnAdminOrGlobalSearchPage
      ? message({
        id: 'Workspace',
        comment: 'Workspace placeholder name for dropdown selector',
      })
      : getWorkspaceName(currentWorkspace);

    return (
      <>
        <WorkspaceIcon
          className={style.WorkspaceButtonIcon}
          width="18px"
          height="18px"
        />
        {(isMobile || expanded) && <span>{workspaceName}</span>}
      </>
    );
  }, [currentWorkspace,
    expanded,
    isMobile,
    message,
    pathname,
    getWorkspaceName,
    isOnAdminPage,
    isOnGlobalSearchPage,
  ]);

  const changeCurrentWorkspace = useCallback((workspaceId: number, triggerLocation: string) => {
    dispatch(selectWorkspace({
      workspaceId,
      pipe: {
        onSuccess: amplitudeLogEvent('Switch Workspace', { triggerLocation }),
      },
    }));

    const isOnContractPage = isUserOnContractPage(pathname);
    // Logic handles when changing the workspace when in contract/template view
    // The saga navigateToWorkspace, is called when opening a contract which means that
    // if this logic is apart of the saga, it will directly redirect back to contract list
    if (isOnGlobalSearchPage || isOnAdminPage) {
      window.location.assign(`/c/${workspaceId}/contracts/all`);
    }

    if (isOnContractsPage && hasSearchQuery) {
      history.push(`/c/${workspaceId}/contracts/all`);
    }

    if (isOnContractPage || isOnTemplatePage) {
      const link = workspaceLocation('/contracts');
      window.location.assign(link);
    }
  }, [
    dispatch,
    hasSearchQuery,
    history,
    isOnAdminPage,
    isOnContractsPage,
    isOnGlobalSearchPage,
    isOnTemplatePage,
    workspaceLocation,
    pathname,
  ]);

  const handleChange = useCallback(
    (workspaceId: number, triggerLocation: string) => {
      toggleMobileMenu();
      changeCurrentWorkspace(workspaceId, triggerLocation);
    },
    [toggleMobileMenu, changeCurrentWorkspace],
  );

  if (isMobile || expanded) {
    return (
      <SelectDropdown
        customButtonClass={style.WorkspaceButton}
        customDropdownClass={style.WorkspaceDropdown}
        className={style.WorkspaceSelectDropdown}
        emptyState={renderEmptyState()}
        fullWidth
        onChange={handleChange}
        options={workspaces.map(toDropdownOption)}
        selectedItem={renderSelectedItem()}
        location="Main navigation"
      />
    );
  }

  return (
    <SidebarTooltip sideOffset={15} tooltipText={tooltipText}>
      <SelectDropdown
        customButtonClass={style.WorkspaceButton}
        customDropdownClass={style.WorkspaceDropdown}
        className={style.WorkspaceSelectDropdown}
        emptyState={renderEmptyState()}
        fullWidth
        onChange={changeCurrentWorkspace}
        options={workspaces.map(toDropdownOption)}
        selectedItem={renderSelectedItem()}
        location="Main navigation"
      />
    </SidebarTooltip>
  );
};

export default localize<Props>(WorkspaceDropdownComponent);
